import {
  ManagedAccountFeeAnalysisContainerDTO,
  SelectedSubProductDTO,
} from "@models/managed-accounts/entities/step/fee";
import { FeesDisplayMode } from "@models/platform-analysis/enums/fee/displayMode";
import { FeesPerformanceScope } from "@models/platform-analysis/enums/fee/feesPerformanceScope";
import { PageTemplate } from "../../../model";
import { FeesDisplayStyle } from "@models/platform-analysis/enums/fee/displayStyle";
import { PAGE_BODY_HEIGHT_WITHOUT_HEADING } from "../../../constant";
import { PDFPortfolioFeeTable } from ".";
import { chunk, cloneDeep } from "lodash";
import { pagesHandler } from "../../../util";
import { FeeAnalysisTableRow } from "@models/managed-accounts/entities/analysis";
import { flatManagedAccountFeeAnalysisData } from "@pages/managed-accounts/_id/steps/fee-analysis/util";
import { PortfolioFeesConstants } from "@pages/platform-analysis/_id/steps/fee/analysis/constants";
import { PDFPortfolioFeeTableConfigs as Configs } from "./configs";
import { shouldHideSubProductName } from "@pages/platform-analysis/util";
import { AnalysisFeePortfolioSetupType } from "@models/platform-analysis/enums/fee/portfolioSetupType";

export const convertToPDFSmaFeeTable = (
  smaPortfolioFee: ManagedAccountFeeAnalysisContainerDTO | undefined,
  tableDisplayMode: FeesDisplayMode | undefined,
  tablePerformanceScope: FeesPerformanceScope | undefined,
) => {
  const { rows } = flatManagedAccountFeeAnalysisData({
    sections: cloneDeep(smaPortfolioFee?.sections),
    isVariableHidden: (variableName: string) => {
      const isInSummaryMode = tableDisplayMode === FeesDisplayMode.Summary;
      const isExcludingPerformanceFees =
        tablePerformanceScope === FeesPerformanceScope.Excluding;

      const shouldHideInSummaryMode =
        isInSummaryMode &&
        !PortfolioFeesConstants.SummaryTableRowVariableNames.includes(
          variableName,
        );

      const shouldHidePerformanceFee =
        isExcludingPerformanceFees &&
        PortfolioFeesConstants.ExcludingPerformanceFeeVariableNames.includes(
          variableName,
        );

      return shouldHideInSummaryMode || shouldHidePerformanceFee;
    },
  });

  return rows;
};

const getEstimatedRowHeight = ({
  rowName,
}: {
  rowName: string;
}): {
  estimatedHeight: number;
  numberOfLines: number;
} => {
  const numberOfLines = Math.ceil(
    rowName.length / Configs.DefaultMaxCharsPerLine,
  );

  return {
    estimatedHeight:
      Configs.DefaultRowHeight +
      Configs.RowHeightLinesMultiplier * (numberOfLines - 1),
    numberOfLines: numberOfLines,
  };
};

const getSelectedRowsHeight = ({
  startCursorIndex,
  rowFlatMap,
  spaceAvailable = PAGE_BODY_HEIGHT_WITHOUT_HEADING,
}: {
  startCursorIndex: number;
  rowFlatMap: FeeAnalysisTableRow[];
  spaceAvailable?: number;
}): {
  sumHeight: number;
  endIndex: number;
} => {
  let rowSumHeightLocal = Configs.TableHeaderHeight + Configs.TableTitleHeight;
  let currentCursorIndex = startCursorIndex;

  while (rowSumHeightLocal < spaceAvailable) {
    const row = rowFlatMap[currentCursorIndex];

    if (!row) break;

    const rowName = row.name || "";
    const { estimatedHeight, numberOfLines } = getEstimatedRowHeight({
      rowName,
    });
    const isLastRow = currentCursorIndex === rowFlatMap.length - 1;
    const shouldIncludeLastRow = isLastRow && numberOfLines === 1;

    if (
      rowSumHeightLocal + estimatedHeight > spaceAvailable &&
      !shouldIncludeLastRow
    )
      break;

    rowSumHeightLocal += estimatedHeight;
    currentCursorIndex++;
  }

  return {
    sumHeight: rowSumHeightLocal,
    endIndex: currentCursorIndex,
  };
};

const handleAppendTable = ({
  pages,
  rows,
  selectedProducts = [],
  startSliceProductIndex,
  endSliceProductIndex,
  spaceAvailable,
  isFollowingTable,
  isLastTable,
  tableViewMode,
  tablePerformanceScope,
  numOfHiddenProducts = 0,
}: {
  pages: PageTemplate[];
  portfolioCompositionData?: any;
  rows: FeeAnalysisTableRow[];
  selectedProducts: SelectedSubProductDTO[];
  startSliceProductIndex: number;
  endSliceProductIndex: number;
  spaceAvailable: number;
  isFollowingTable?: boolean;
  isLastTable?: boolean;
  tableViewMode?: FeesDisplayStyle;
  tablePerformanceScope?: FeesPerformanceScope;
  numOfHiddenProducts?: number;
}) => {
  const selectedProductsLocal = selectedProducts.slice(
    startSliceProductIndex,
    endSliceProductIndex,
  );

  let rowCursorIndex = 0;
  let spaceAvailableAfterAppend = spaceAvailable;

  while (rowCursorIndex < rows.length) {
    const startSlice = rowCursorIndex;
    const spaceForCalculation = spaceAvailableAfterAppend;
    const { endIndex, sumHeight } = getSelectedRowsHeight({
      startCursorIndex: rowCursorIndex,
      rowFlatMap: rows,
      spaceAvailable: spaceForCalculation,
    });

    const isEntireTableFit = endIndex === rows.length;

    const finalCalculation = isEntireTableFit
      ? { endIndex, sumHeight }
      : getSelectedRowsHeight({
          startCursorIndex: rowCursorIndex,
          rowFlatMap: rows,
          spaceAvailable: PAGE_BODY_HEIGHT_WITHOUT_HEADING,
        });

    const countItemTitle =
      selectedProductsLocal?.length === 1
        ? `(Product ${startSliceProductIndex + 1} of ${
            selectedProducts?.length ?? 0
          })`
        : `(Products ${startSliceProductIndex + 1}-${endSliceProductIndex} of ${
            selectedProducts?.length ?? 0
          })`;

    const showNumOfHiddenProducts =
      numOfHiddenProducts > 0 &&
      isLastTable &&
      finalCalculation.endIndex === rows.length;

    const portfolioCompositionComp = (
      <PDFPortfolioFeeTable
        isAppending={isEntireTableFit}
        data={rows.slice(startSlice, finalCalculation.endIndex)}
        selectedSubProducts={selectedProductsLocal}
        tableViewMode={tableViewMode}
        tablePerformanceScope={tablePerformanceScope}
        isLastTable={isLastTable}
        isFollowingTable={isFollowingTable}
        countItemTitle={countItemTitle}
        numOfHiddenProducts={
          showNumOfHiddenProducts ? numOfHiddenProducts : undefined
        }
      />
    );

    pagesHandler(pages, portfolioCompositionComp, isEntireTableFit);

    rowCursorIndex = finalCalculation.endIndex;
    spaceAvailableAfterAppend =
      (isEntireTableFit
        ? spaceAvailableAfterAppend
        : PAGE_BODY_HEIGHT_WITHOUT_HEADING) -
      finalCalculation.sumHeight -
      Configs.TableFooterHeight;
  }

  return spaceAvailableAfterAppend;
};

export const portfolioFeeTablePageHandler = ({
  pages,
  spaceAvailable,
  smaPortfolioFee,
  hiddenProductIndexMap,
  tableRows,
  tableViewMode,
  tablePerformanceScope,
}: {
  pages: PageTemplate[];
  spaceAvailable: number;
  hiddenProductIndexMap: boolean[];
  smaPortfolioFee: ManagedAccountFeeAnalysisContainerDTO;
  tableRows: FeeAnalysisTableRow[];
  tableViewMode?: FeesDisplayStyle;
  tablePerformanceScope?: FeesPerformanceScope;
}) => {
  if (!smaPortfolioFee) return spaceAvailable;

  const selectedProducts =
    smaPortfolioFee?.selectedSubProducts
      ?.map((item, itemIndex) => {
        const { productName, name: setupName, setupType } = item || {};
        const shouldHideName = shouldHideSubProductName(
          setupType as AnalysisFeePortfolioSetupType,
          productName,
        );

        const product: SelectedSubProductDTO = {
          ...item,
          name: !shouldHideName ? setupName : undefined,
          isHidden: hiddenProductIndexMap[itemIndex],
        };

        return product;
      })
      ?.filter((i) => !i.isHidden) ?? [];

  const numOfSections = chunk(
    selectedProducts,
    Configs.MaxProductsDisplay,
  ).length;
  const numOfHiddenProducts = hiddenProductIndexMap.filter((i) => i).length;
  let spaceAvailableAfterAppend = spaceAvailable;

  Array.from({ length: numOfSections }).forEach((_, i) => {
    const startSlice = i * Configs.MaxProductsDisplay;
    const endSlice = Math.min(
      startSlice + Configs.MaxProductsDisplay,
      selectedProducts.length,
    );

    const sectionRows: FeeAnalysisTableRow[] =
      tableRows?.map((row) => ({
        ...row,
        analysisData: row.analysisData
          ?.filter((_, dataIndex) => !hiddenProductIndexMap[dataIndex])
          ?.slice(startSlice, endSlice),
      })) ?? [];

    spaceAvailableAfterAppend = handleAppendTable({
      selectedProducts: selectedProducts,
      pages,
      spaceAvailable: spaceAvailableAfterAppend,
      startSliceProductIndex: startSlice,
      endSliceProductIndex: endSlice,
      isFollowingTable: i > 0,
      isLastTable: i === numOfSections - 1,
      rows: sectionRows,
      tableViewMode,
      tablePerformanceScope,
      numOfHiddenProducts: numOfHiddenProducts,
    });
  });

  if (numOfSections === 0) {
    const entireSectionHeight = Configs.NoDataSectionHeight;
    const shouldAppend = spaceAvailable >= entireSectionHeight;
    const comp = (
      <PDFPortfolioFeeTable
        isAppending={shouldAppend}
        data={[]}
        selectedSubProducts={[]}
        tableViewMode={tableViewMode}
        tablePerformanceScope={tablePerformanceScope}
        numOfHiddenProducts={numOfHiddenProducts}
      />
    );

    pagesHandler(pages, comp, shouldAppend);

    spaceAvailableAfterAppend = shouldAppend
      ? spaceAvailable - Configs.TableTitleHeight - 60
      : PAGE_BODY_HEIGHT_WITHOUT_HEADING - Configs.TableTitleHeight - 60;
  }

  return spaceAvailableAfterAppend - Configs.SectionGapHeight;
};
